import React, { useState, useCallback } from 'react';
import { Alert, IconButton, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import {
  Edit,
  Delete,
  Search,
  GroupAdd,
  FilterList,
  Group,
  Visibility,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import type { Team } from '../types';
import { useAuth } from '../contexts/AuthContext';
import GenericFilterModal, { FilterField } from '../components/GenericFilterModal';
import { useFilterState } from '../hooks/useFilterState';
import { 
  PageLayout, 
  PageHeader, 
  ScrollableContent,
  FilterChip
} from '../components/Layout/index';
import ViewModal, { ViewField } from '../components/ViewModal';
import { 
  Button, 
  Chip, 
  DataTable,
  colors,
  spacing,
  typography,
  useStatusColors,
  ActionButtons
} from '../design-system';
import type { Column } from '../design-system/components/organisms/DataTable/DataTable';

interface CreateTeamRequest {
  name: string;
  description?: string;
  isActive?: boolean;
}

const Teams: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Stati locali per i filtri (senza debounce)
  const [localFilters, setLocalFilters] = useState({
    search: '',
    isActive: null as boolean | null,
  });
  
  // Stati per la query (con debounce) - inizializzati vuoti per non filtrare all'avvio
  const [queryFilters, setQueryFilters] = useState({
    search: '',
    isActive: null as boolean | null,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingTeam, setViewingTeam] = useState<Team | null>(null);
  const [formData, setFormData] = useState<CreateTeamRequest>({
    name: '',
    description: '',
    isActive: true,
  });

  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const statusColors = useStatusColors();

  // Hook per gestire lo stato dei filtri
  const { hasActiveFilters, activeFiltersCount } = useFilterState(queryFilters);

  // Query per recuperare i team
  const { data: teamsData, isLoading: teamsLoading, error: teamsError } = useQuery({
    queryKey: ['teams', { page: page + 1, limit: rowsPerPage, ...queryFilters }],
    queryFn: () => apiService.getTeams({ 
      page: page + 1, 
      limit: rowsPerPage, 
      search: queryFilters.search || undefined,
      isActive: queryFilters.isActive,
    }),
    staleTime: 5 * 60 * 1000, // 5 minuti
  });

  // Configurazione campi per la modale filtri
  const filterFields: FilterField[] = [
    {
      key: 'search',
      label: 'Cerca',
      type: 'text',
      placeholder: 'Nome team...',
    },
    {
      key: 'isActive',
      label: 'Stato',
      type: 'select',
      options: [
        { value: '', label: 'Tutti' },
        { value: 'true', label: 'Attivo' },
        { value: 'false', label: 'Inattivo' },
      ],
    },
  ];

  const filterValues = {
    search: localFilters.search,
    isActive: localFilters.isActive?.toString() || '',
  };

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateTeamRequest) => apiService.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateTeamRequest }) => 
      apiService.updateTeam(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  // Funzioni per i filtri
  const updateFilter = (fieldId: string, value: any) => {
    setLocalFilters(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const applyFilters = () => {
    setQueryFilters({
      search: localFilters.search,
      isActive: localFilters.isActive,
    });
    setFilterModalOpen(false);
  };

  const clearFilters = () => {
    setLocalFilters({
      search: '',
      isActive: null,
    });
    setQueryFilters({
      search: '',
      isActive: null,
    });
    setFilterModalOpen(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      isActive: true,
    });
  };

  const handleOpenDialog = (team?: Team) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        name: team.name,
        description: team.description || '',
        isActive: team.isActive,
      });
    } else {
      setEditingTeam(null);
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeam(null);
    resetForm();
  };

  const handleOpenViewModal = (team: Team) => {
    setViewingTeam(team);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewingTeam(null);
  };

  const getTeamViewFields = (team: Team): ViewField[] => [
    {
      key: 'name',
      label: 'Nome Team',
      value: team.name,
      type: 'text',
    },
    {
      key: 'description',
      label: 'Descrizione',
      value: team.description,
      type: 'text',
    },
    {
      key: 'isActive',
      label: 'Stato',
      value: team.isActive ? 'Attivo' : 'Inattivo',
      type: 'chip',
      chipColor: team.isActive ? 'success' : 'error',
    },
    {
      key: 'collectors',
      label: 'Raccoglitori',
      value: team.collectors?.length || 0,
      type: 'text',
    },
    {
      key: 'createdAt',
      label: 'Data Creazione',
      value: team.createdAt,
      type: 'date',
    },
    {
      key: 'updatedAt',
      label: 'Ultima Modifica',
      value: team.updatedAt,
      type: 'date',
    },
  ];

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (editingTeam) {
      updateMutation.mutate({ id: editingTeam.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo team?')) {
      deleteMutation.mutate(id);
    }
  };

  if (!hasRole('ADMIN')) {
    return (
      <Box>
        <Alert severity="error">
          Accesso negato. Solo gli amministratori possono gestire i team.
        </Alert>
      </Box>
    );
  }

  const teams = teamsData?.data || [];
  const totalCount = teamsData?.pagination?.total || 0;

  // Definizione colonne per DataTable
  const columns: Column<Team>[] = [
    {
      id: 'name',
      label: 'Nome Team',
      minWidth: 200,
      format: (value, team) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Group sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="body2" fontWeight={500}>
            {team.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'description',
      label: 'Descrizione',
      minWidth: 250,
      format: (value, team) => (
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {team.description || 'Nessuna descrizione'}
        </Typography>
      ),
    },
    {
      id: 'isActive',
      label: 'Stato',
      minWidth: 120,
      align: 'center',
      format: (value, team) => {
        const colors = team.isActive ? statusColors.active : statusColors.inactive;
        return (
          <Chip
            label={team.isActive === true ? 'Attivo' : team.isActive === false ? 'Inattivo' : 'N/A'}
            variant={team.isActive ? 'default' : 'outlined'}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              backgroundColor: colors.backgroundColor,
              color: colors.color,
              borderColor: colors.borderColor,
              ...(team.isActive ? {
                // Chip attivo con alpha
                backgroundColor: colors.backgroundColor,
                color: colors.color,
                borderColor: colors.borderColor,
              } : {
                // Chip inattivo normale
                backgroundColor: colors.backgroundColor,
                color: colors.color,
                borderColor: colors.borderColor,
              }),
            }}
          />
        );
      },
    },
    {
      id: 'createdAt',
      label: 'Data Creazione',
      minWidth: 150,
      format: (value, team) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(team.createdAt).toLocaleDateString('it-IT')}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Azioni',
      minWidth: 120,
      align: 'center',
      format: (value, team) => (
        <ActionButtons
          onView={() => handleOpenViewModal(team)}
          onEdit={() => handleOpenDialog(team)}
          onDelete={() => handleDelete(team.id)}
          size="small"
          spacing={0.5}
        />
      ),
    },
  ];

  return (
    <PageLayout>
      <PageHeader>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Team</Typography>
          <Box display="flex" gap={2}>
            <FilterChip
              onClick={() => setFilterModalOpen(true)}
              hasActiveFilters={hasActiveFilters}
              activeFiltersCount={activeFiltersCount}
            />
            <Button
              variant="primary"
              startIcon={<GroupAdd />}
              onClick={() => handleOpenDialog()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Nuovo Team
            </Button>
          </Box>
        </Box>
      </PageHeader>

      <ScrollableContent>
        <DataTable
          columns={columns}
          data={teams}
          loading={teamsLoading}
          error={teamsError ? `Errore nel caricamento dei team: ${teamsError.message || teamsError}` : undefined}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage="Nessun team trovato"
        />
      </ScrollableContent>

      {/* Modale per creare/modificare team */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTeam ? 'Modifica Team' : 'Nuovo Team'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                  fullWidth
                  label="Nome Team"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Es. Team Milano Centro"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                  fullWidth
                  label="Descrizione"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  placeholder="Descrizione del team e delle sue responsabilità..."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                    label="Stato"
                  >
                    <MenuItem value="active">Attivo</MenuItem>
                    <MenuItem value="inactive">Inattivo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Annulla
            </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={createMutation.isPending || updateMutation.isPending}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {createMutation.isPending || updateMutation.isPending ? 'Salvataggio...' : (editingTeam ? 'Aggiorna' : 'Crea')}
          </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* GenericFilterModal */}
      <GenericFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filtri Team"
        fields={filterFields}
        values={filterValues}
        onUpdateValue={updateFilter}
        onApply={applyFilters}
        onClear={clearFilters}
      />

      {/* Modale Visualizzazione Dettagli */}
      <ViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        title="Dettagli Team"
        fields={viewingTeam ? getTeamViewFields(viewingTeam) : []}
        actions={
          <Box>
            <Button
              variant="outlined"
              onClick={handleCloseViewModal}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                mr: 1,
              }}
            >
              Chiudi
            </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleCloseViewModal();
              handleOpenDialog(viewingTeam);
            }}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Modifica
          </Button>
          </Box>
        }
      />
    </PageLayout>
  );
};

export default Teams;
